import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BedDouble,
  Bath,
  Ruler,
  Car,
  PawPrint,
  Sofa,
  MapPin,
  CalendarClock,
  Phone,
} from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { VideoEmbed } from "@/components/rentals/VideoEmbed";
import { ViewingModal } from "@/components/rentals/ViewingModal";
import {
  getListing,
  getCity,
  getType,
  STATUS_LABEL,
  listingsBy,
} from "@/data/rentals";

const money = (n) => `$${n.toLocaleString("en-CA")}`;

/**
 * /rentals/:city/:type/:slug — full property detail page.
 */
export default function RentalDetail() {
  const { city: citySlug, type: typeSlug, slug } = useParams();
  const listing = getListing(slug);
  const city = getCity(citySlug);
  const type = getType(typeSlug);
  const [modalOpen, setModalOpen] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);

  if (!listing || !city || !type) return <Navigate to="/rentals" replace />;
  // guard against mismatched URLs (share links should still resolve correctly)
  if (listing.city !== city.slug || listing.type !== type.slug) {
    return (
      <Navigate
        to={`/rentals/${listing.city}/${listing.type}/${listing.slug}`}
        replace
      />
    );
  }

  const gallery = [listing.hero, ...(listing.gallery || [])];
  const status = STATUS_LABEL[listing.status] || STATUS_LABEL.available;
  const similar = listingsBy({ city: city.slug, type: type.slug })
    .filter((l) => l.slug !== listing.slug)
    .slice(0, 3);

  return (
    <div data-testid={`page-rental-detail-${listing.slug}`}>
      {/* ============ HERO (gallery) ============ */}
      <header
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 pt-[128px] md:px-10 md:pt-[168px]">
          <Reveal>
            <div className="flex items-center gap-2">
              <Link
                to={`/rentals/${city.slug}/${type.slug}`}
                className="group inline-flex items-center gap-2"
                style={{ color: "rgba(32,30,26,0.7)" }}
                data-testid="back-to-type"
              >
                <ArrowLeft size={16} />
                <span className="label-caps text-[11px]">
                  {city.name} · {type.name}
                </span>
              </Link>
            </div>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 items-end gap-6 md:grid-cols-12 md:gap-8">
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <span
                  className="label-caps rounded-full px-3 py-1 text-[10px]"
                  style={{
                    backgroundColor: status.bg,
                    color: status.color,
                  }}
                  data-testid="listing-status"
                >
                  {status.label}
                </span>
              </Reveal>
              <Reveal delay={0.08}>
                <h1
                  className="font-display mt-5"
                  style={{
                    fontSize: "clamp(44px, 6.5vw, 96px)",
                    color: "#201E1A",
                    lineHeight: 0.95,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="listing-title"
                >
                  {listing.name}{" "}
                  <span
                    className="italic"
                    style={{ color: "rgba(32,30,26,0.45)" }}
                  >
                    {listing.unit}
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p
                  className="mt-4 inline-flex items-center gap-2 text-[15px]"
                  style={{ color: "rgba(32,30,26,0.7)" }}
                >
                  <MapPin size={15} />
                  {listing.address}
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.15}>
                <p
                  className="font-display"
                  style={{
                    fontSize: "clamp(36px, 4vw, 56px)",
                    color: "#24382E",
                    lineHeight: 0.9,
                    letterSpacing: "-0.01em",
                  }}
                  data-testid="listing-price"
                >
                  {money(listing.monthlyRent)}
                  <span
                    className="ml-1 text-[16px]"
                    style={{ color: "rgba(32,30,26,0.5)" }}
                  >
                    / mo
                  </span>
                </p>
                <p
                  className="label-caps mt-2 text-[10px]"
                  style={{ color: "rgba(32,30,26,0.55)" }}
                >
                  {listing.availableDate === "—"
                    ? "Not currently available"
                    : `Available ${listing.availableDate}`}
                </p>
              </Reveal>
            </div>
          </div>

          {/* ---- GALLERY ---- */}
          <Reveal delay={0.18} className="mt-10">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5 md:gap-4">
              <div className="relative overflow-hidden md:col-span-3">
                <motion.img
                  key={gallery[heroIdx]}
                  src={gallery[heroIdx]}
                  alt={`${listing.name} — main view`}
                  className="block w-full object-cover"
                  style={{
                    aspectRatio: "4 / 3",
                    borderRadius: 18,
                    maxHeight: 620,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  data-testid="listing-hero-image"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 md:col-span-2 md:grid-cols-2 md:gap-4">
                {gallery.slice(0, 4).map((src, i) => (
                  <button
                    key={src + i}
                    type="button"
                    onClick={() => setHeroIdx(i)}
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "4 / 3",
                      borderRadius: 14,
                      outline:
                        i === heroIdx
                          ? "2px solid #24382E"
                          : "1px solid rgba(32,30,26,0.1)",
                      outlineOffset: i === heroIdx ? 2 : 0,
                    }}
                    data-testid={`listing-thumb-${i}`}
                    aria-label={`View photo ${i + 1}`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {/* ============ SPECS + BOOKING ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-24">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-14">
            {/* left column: specs + description */}
            <div className="md:col-span-7">
              <Reveal>
                <Eyebrow>The place</Eyebrow>
              </Reveal>
              <Reveal delay={0.04}>
                <h2
                  className="font-display mt-5"
                  style={{
                    fontSize: "clamp(30px, 3.6vw, 46px)",
                    color: "#201E1A",
                    lineHeight: 1.05,
                  }}
                >
                  {listing.neighbourhood}, in one look.
                </h2>
              </Reveal>

              <div
                className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6 border-t pt-8 md:grid-cols-3"
                style={{ borderColor: "rgba(32,30,26,0.12)" }}
                data-testid="listing-specs"
              >
                <Spec icon={BedDouble} label="Bedrooms" value={listing.specs.beds} />
                <Spec icon={Bath} label="Bathrooms" value={listing.specs.baths} />
                <Spec icon={Ruler} label="Sqft" value={listing.specs.sqft} />
                <Spec icon={Car} label="Parking" value={listing.specs.parking} />
                <Spec icon={PawPrint} label="Pets" value={listing.specs.pets} />
                <Spec
                  icon={Sofa}
                  label="Furnished"
                  value={listing.specs.furnished ? "Yes" : "No"}
                />
              </div>

              <div className="mt-12">
                {listing.description.map((p, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <p
                      className={`text-[17px] leading-[1.7] ${
                        i > 0 ? "mt-5" : ""
                      }`}
                      style={{ color: "rgba(32,30,26,0.82)" }}
                    >
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>

              {listing.amenities?.length > 0 && (
                <div className="mt-14">
                  <Reveal>
                    <Eyebrow>Amenities</Eyebrow>
                  </Reveal>
                  <ul
                    className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2"
                    data-testid="listing-amenities"
                  >
                    {listing.amenities.map((a, i) => (
                      <Reveal key={a} as="li" delay={i * 0.02}>
                        <span
                          className="flex items-start gap-3 text-[15px]"
                          style={{ color: "rgba(32,30,26,0.78)" }}
                        >
                          <span
                            className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: "#C89A4E" }}
                          />
                          {a}
                        </span>
                      </Reveal>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* right column: sticky booking card */}
            <div className="md:col-span-5">
              <div className="md:sticky md:top-24">
                <Reveal>
                  <div
                    className="rounded-[18px] border p-7"
                    style={{
                      borderColor: "rgba(32,30,26,0.1)",
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 20px 60px -30px rgba(20,35,27,0.35)",
                    }}
                    data-testid="listing-booking-card"
                  >
                    <p
                      className="label-caps text-[10px]"
                      style={{ color: "#C89A4E" }}
                    >
                      Interested?
                    </p>
                    <p
                      className="font-display mt-3"
                      style={{
                        fontSize: 36,
                        color: "#201E1A",
                        lineHeight: 1,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {money(listing.monthlyRent)}
                      <span
                        className="ml-1 text-[15px]"
                        style={{ color: "rgba(32,30,26,0.5)" }}
                      >
                        / month
                      </span>
                    </p>

                    <dl
                      className="mt-6 grid grid-cols-2 gap-x-4 gap-y-4 border-t pt-6 text-[13px]"
                      style={{ borderColor: "rgba(32,30,26,0.1)" }}
                    >
                      <MetaRow label="Available" value={listing.availableDate} />
                      <MetaRow label="Lease" value={listing.leaseTerm} />
                      <MetaRow
                        label="Deposit"
                        value={money(listing.deposit)}
                      />
                      <MetaRow
                        label="Utilities"
                        value={
                          listing.utilities?.length
                            ? listing.utilities.join(", ")
                            : "Tenant-paid"
                        }
                      />
                    </dl>

                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className="pill pill-pine mt-8 w-full text-[15px]"
                      disabled={listing.status === "leased"}
                      data-testid="listing-book-viewing"
                    >
                      <CalendarClock size={16} className="mr-2 inline" />
                      {listing.status === "leased"
                        ? "Currently leased"
                        : "Book a viewing"}
                    </button>

                    <a
                      href="tel:+15879001234"
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 text-[13px]"
                      style={{ color: "rgba(32,30,26,0.65)" }}
                      data-testid="listing-call"
                    >
                      <Phone size={13} />
                      Or call (587) 900-1234
                    </a>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VIDEOS ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
        data-testid="listing-videos"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <Reveal>
            <Eyebrow>Walk it, first</Eyebrow>
          </Reveal>
          <Reveal delay={0.04}>
            <h2
              className="font-display mt-6 max-w-[18ch]"
              style={{
                fontSize: "clamp(36px, 4.6vw, 60px)",
                color: "#201E1A",
                lineHeight: 1.02,
              }}
            >
              A tour of the home,{" "}
              <span className="italic" style={{ color: "#C89A4E" }}>
                and the block.
              </span>
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
            <Reveal>
              <div>
                <p
                  className="label-caps text-[10px]"
                  style={{ color: "rgba(32,30,26,0.6)" }}
                >
                  Property tour
                </p>
                <h3
                  className="font-display mt-2"
                  style={{ fontSize: 22, color: "#201E1A", lineHeight: 1.1 }}
                >
                  Inside {listing.name} {listing.unit}
                </h3>
                <div className="mt-5">
                  <VideoEmbed
                    url={listing.propertyVideoUrl}
                    title="Property tour"
                    testId="listing-property-video"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div>
                <p
                  className="label-caps text-[10px]"
                  style={{ color: "rgba(32,30,26,0.6)" }}
                >
                  Community walk
                </p>
                <h3
                  className="font-display mt-2"
                  style={{ fontSize: 22, color: "#201E1A", lineHeight: 1.1 }}
                >
                  {listing.neighbourhood}, on foot
                </h3>
                <div className="mt-5">
                  <VideoEmbed
                    url={listing.communityVideoUrl}
                    title="Community tour"
                    testId="listing-community-video"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SIMILAR ============ */}
      {similar.length > 0 && (
        <section
          className="relative"
          style={{ backgroundColor: "#F8F5EF" }}
          data-nav-theme="light"
        >
          <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
            <div className="flex items-end justify-between gap-6">
              <div>
                <Reveal>
                  <Eyebrow>More in {city.name}</Eyebrow>
                </Reveal>
                <Reveal delay={0.04}>
                  <h2
                    className="font-display mt-5"
                    style={{
                      fontSize: "clamp(30px, 3.6vw, 46px)",
                      color: "#201E1A",
                      lineHeight: 1.05,
                    }}
                  >
                    Other {type.name.toLowerCase()} nearby
                  </h2>
                </Reveal>
              </div>
              <Link
                to={`/rentals/${city.slug}/${type.slug}`}
                className="label-caps hidden text-[11px] underline sm:inline-flex"
                style={{ color: "#24382E" }}
                data-testid="see-all-similar"
              >
                See all
              </Link>
            </div>

            <ul className="mt-12 grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {similar.map((l, i) => (
                <Reveal key={l.slug} as="li" delay={i * 0.05}>
                  <Link
                    to={`/rentals/${city.slug}/${type.slug}/${l.slug}`}
                    className="group block"
                    data-testid={`similar-${l.slug}`}
                  >
                    <div
                      className="relative overflow-hidden"
                      style={{ aspectRatio: "4 / 5", borderRadius: 14 }}
                    >
                      <img
                        src={l.hero}
                        alt={l.name}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                    <h3
                      className="font-display mt-4"
                      style={{ fontSize: 20, color: "#201E1A", lineHeight: 1.1 }}
                    >
                      {l.name}{" "}
                      <span style={{ color: "rgba(32,30,26,0.45)" }}>
                        {l.unit}
                      </span>
                    </h3>
                    <p
                      className="mt-1 text-[13px]"
                      style={{ color: "rgba(32,30,26,0.6)" }}
                    >
                      {l.neighbourhood} · {money(l.monthlyRent)}/mo
                    </p>
                  </Link>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="w-full opacity-70" aria-hidden>
            <Ridgeline stroke="#C89A4E" height={90} />
          </div>
        </section>
      )}

      <ViewingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        listing={listing}
      />
    </div>
  );
}

const Spec = ({ icon: Icon, label, value }) => (
  <div>
    <span
      className="label-caps text-[10px] inline-flex items-center gap-1.5"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      <Icon size={12} />
      {label}
    </span>
    <p
      className="mt-2 font-display"
      style={{
        fontSize: 22,
        color: "#201E1A",
        lineHeight: 1.1,
        letterSpacing: "-0.005em",
      }}
    >
      {value}
    </p>
  </div>
);

const MetaRow = ({ label, value }) => (
  <div>
    <dt
      className="label-caps text-[10px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {label}
    </dt>
    <dd
      className="mt-1 text-[14px]"
      style={{ color: "#201E1A" }}
    >
      {value}
    </dd>
  </div>
);
